import { useState } from "react";
import api from "../lib/api";
import { usePipeline } from "../lib/pipeline";
import { useFetch, Card, Badge } from "../components/Shared";
import PageHeader, { PageBody } from "../components/Shared";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Phone, WarningCircle, PlugsConnected,
  Copy, CheckCircle, Info, ShieldCheck, Eye, EyeSlash, Sparkle,
} from "@phosphor-icons/react";

const STATUS_TONES = { active: "forest", pending: "warning", inactive: "signal" };
const QUALITY_TONES = { GREEN: "forest", YELLOW: "warning", RED: "signal" };

const WEBHOOK_PATH = "/api/whatsapp/webhook/dialog360";
const META_TEMPLATES_WEBHOOK_PATH = "/api/whatsapp/webhook/meta-templates";

export default function WhatsAppNumbers() {
  const { data, loading, reload } = useFetch(() => api.get("/whatsapp-numbers").then((r) => r.data), []);
  const [edit, setEdit] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const items = data || [];

  const webhookBaseUrl = `${process.env.REACT_APP_BACKEND_URL}${WEBHOOK_PATH}`;
  const metaTemplatesWebhookUrl = `${process.env.REACT_APP_BACKEND_URL}${META_TEMPLATES_WEBHOOK_PATH}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copiado`),
      () => toast.error("No se pudo copiar"),
    );
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este número? Los mensajes ya enviados no se borran.")) return;
    try { await api.delete(`/whatsapp-numbers/${id}`); toast.success("Número eliminado"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al eliminar"); }
  };

  const testConn = async (id) => {
    try {
      const { data: r } = await api.post(`/whatsapp-numbers/${id}/test-connection`);
      toast[r.ok ? "success" : "error"](`${r.status}: ${r.message}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <>
      <PageHeader
        label="Sistema · Multi-WhatsApp"
        title="Números de WhatsApp"
        description="Multi-360Dialog · uno o más por pipeline. El routing automático envía cada inbound al pipeline correcto según el phone_number_id por el que entra."
      >
        <button onClick={() => setShowCreate(true)} data-testid="add-wa-number-btn"
          className="bg-terracotta text-white text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-terracotta-hover">
          <Plus size={14} /> Agregar número
        </button>
      </PageHeader>
      <PageBody>
        {/* Webhook setup banner — guía operativa */}
        <Card className="p-5 mb-6 border-l-4 border-l-terracotta" testid="webhook-setup-card">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} className="text-terracotta mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="section-label mb-1">Setup · Webhook único para todos los números</div>
              <h3 className="font-heading text-lg mb-2">Configura esta URL en 360Dialog Business Manager para cada número</h3>
              <p className="text-sm text-carbon-600 mb-3 leading-relaxed">
                360Dialog enruta automáticamente los inbound de los <strong>dos números</strong> a esta misma URL.
                Internamente identificamos el número por el <code className="font-mono text-xs bg-page px-1 py-0.5 rounded">phone_number_id</code> del payload
                y asignamos al pipeline correcto. <strong>Es indispensable que cada número tenga su <code className="font-mono text-xs bg-page px-1 py-0.5 rounded">phone_number_id</code> guardado abajo</strong>.
              </p>
              <div className="bg-page border border-carbon-200 rounded p-3 flex items-center gap-2 font-mono text-xs" data-testid="webhook-url-box">
                <span className="text-carbon-500 shrink-0">POST</span>
                <span className="text-carbon-900 flex-1 truncate">{webhookBaseUrl}</span>
                <button onClick={() => copy(webhookBaseUrl, "Webhook URL")} data-testid="copy-webhook-url"
                  className="text-xs px-2.5 py-1 border border-carbon-200 text-carbon-700 rounded hover:bg-white flex items-center gap-1.5 shrink-0">
                  <Copy size={12} /> Copiar
                </button>
              </div>
              <ul className="text-xs text-carbon-500 mt-3 space-y-1 list-disc list-inside">
                <li>En 360Dialog, ve a <strong>Webhooks</strong> de cada número y pega esta URL.</li>
                <li>Si configuras <code className="font-mono">webhook_secret</code> por número, también define la misma variable <code className="font-mono">WHATSAPP_WEBHOOK_SECRET</code> en el backend.</li>
                <li>Después de configurar, usa <strong>Probar conexión</strong> en cada tarjeta para validar.</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Meta Templates auto-sync webhook */}
        <Card className="p-5 mb-6 border-l-4 border-l-forest" testid="meta-templates-webhook-card">
          <div className="flex items-start gap-3">
            <Sparkle size={22} className="text-forest mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="section-label mb-1">Auto-sync · Estado de Templates desde Meta</div>
              <h3 className="font-heading text-lg mb-2">Para que las templates aprobadas se actualicen solas en Heroes Migrantes</h3>
              <p className="text-sm text-carbon-600 mb-3 leading-relaxed">
                Cuando Meta aprueba o rechaza una template, te avisa por webhook al campo
                <code className="font-mono text-xs bg-page px-1 py-0.5 rounded mx-1">message_template_status_update</code>.
                Configura esta URL una sola vez y el panel sincroniza automáticamente status, <code className="font-mono text-xs bg-page px-1 py-0.5 rounded">meta_template_id</code>, fecha de aprobación y motivo de rechazo.
              </p>
              <div className="bg-page border border-carbon-200 rounded p-3 flex items-center gap-2 font-mono text-xs mb-2" data-testid="meta-templates-webhook-url-box">
                <span className="text-carbon-500 shrink-0">POST/GET</span>
                <span className="text-carbon-900 flex-1 truncate">{metaTemplatesWebhookUrl}</span>
                <button onClick={() => copy(metaTemplatesWebhookUrl, "Meta Templates webhook URL")} data-testid="copy-meta-templates-webhook-url"
                  className="text-xs px-2.5 py-1 border border-carbon-200 text-carbon-700 rounded hover:bg-white flex items-center gap-1.5 shrink-0">
                  <Copy size={12} /> Copiar
                </button>
              </div>
              <details className="text-xs text-carbon-600 mt-2">
                <summary className="cursor-pointer text-forest font-medium hover:underline">Cómo configurarlo en Meta Business Manager (paso a paso)</summary>
                <ol className="list-decimal list-inside space-y-1.5 mt-2 leading-relaxed text-carbon-700 pl-1">
                  <li>Entra a <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-forest underline">developers.facebook.com/apps</a> → selecciona tu App de WhatsApp.</li>
                  <li>Menú lateral → <strong>WhatsApp</strong> → <strong>Configuration</strong> (o <strong>Webhooks</strong>).</li>
                  <li>En <strong>Webhook</strong>: click <strong>Edit</strong>.</li>
                  <li>Pega <strong>Callback URL</strong> = la URL de arriba.</li>
                  <li><strong>Verify token</strong> = lo que pongas en <code className="font-mono">META_WEBHOOK_VERIFY_TOKEN</code> del backend (ej. <code className="font-mono">hm_verify_2026</code>). Guarda.</li>
                  <li>Meta hará un GET de verificación inmediatamente → si el token coincide, se marca verificado ✅.</li>
                  <li>En <strong>Webhook fields</strong>: subscribe a <code className="font-mono">message_template_status_update</code>. (Opcional: también <code className="font-mono">messages</code> si quieres unificar con el webhook de inbound).</li>
                  <li>Listo. Cada cambio de estado de tus templates va a llegar aquí en segundos.</li>
                </ol>
                <div className="mt-3 bg-page border border-carbon-200 rounded p-2.5">
                  <div className="section-label">Variables que tienes que poner en backend/.env:</div>
                  <pre className="font-mono text-[10.5px] text-carbon-700 mt-1">{`META_WEBHOOK_VERIFY_TOKEN=hm_verify_2026
META_GRAPH_API_TOKEN=EAAxxxxxxxxxx   # System User token con permiso whatsapp_business_management (opcional, para sync manual)
META_WABA_ID=123456789012345          # WhatsApp Business Account ID (opcional)`}</pre>
                </div>
                <p className="mt-2 text-carbon-500">
                  <strong>Si usas 360Dialog Cloud API</strong>: en partner.360dialog.com → tu canal → Webhooks → agrega la misma URL para el field <code className="font-mono">message_template_status_update</code>. El payload tiene la misma forma — el backend lo soporta de ambos lados.
                </p>
              </details>
            </div>
          </div>
        </Card>

        {loading ? <div className="text-carbon-500">Cargando...</div> : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((n) => {
              const missing = [];
              if (!n.phone_number_id) missing.push("phone_number_id");
              if (!n.api_key || n.api_key === "PLACEHOLDER") missing.push("api_key");
              if (n.provider === "dialog360" && !n.phone_number) missing.push("phone_number");
              const ready = missing.length === 0;
              return (
                <Card key={n.id} testid={`wa-card-${n.id}`} className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-heading text-lg flex items-center gap-2">
                        <Phone size={16} className="text-terracotta" />
                        {n.label}
                      </div>
                      <div className="text-sm text-carbon-500 font-mono mt-1">{n.phone_number}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <Badge tone={n.pipeline_slug === "founder" ? "terracotta" : "forest"}>
                        pipeline:{n.pipeline_slug}
                      </Badge>
                      <Badge tone={STATUS_TONES[n.status] || "neutral"}>{n.status}</Badge>
                    </div>
                  </div>

                  {/* Readiness panel */}
                  {ready ? (
                    <div className="bg-forest/5 border border-forest/30 rounded p-2.5 text-xs flex items-center gap-2 mb-3" data-testid={`wa-ready-${n.id}`}>
                      <CheckCircle size={14} className="text-forest shrink-0" />
                      <span className="text-forest">Número listo para enrutar mensajes en este pipeline.</span>
                    </div>
                  ) : (
                    <div className="bg-signal/5 border border-signal/30 rounded p-2.5 text-xs flex items-start gap-2 mb-3" data-testid={`wa-missing-${n.id}`}>
                      <WarningCircle size={14} className="text-signal shrink-0 mt-0.5" />
                      <div>
                        <div className="text-signal font-medium mb-0.5">Falta configurar antes de activar</div>
                        <div className="text-carbon-700">
                          {missing.map((m) => (
                            <span key={m} className="font-mono text-[10.5px] bg-white border border-signal/30 px-1.5 py-0.5 rounded mr-1">{m}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5 text-xs">
                    <Row k="Provider" v={n.provider} />
                    <Row k="Phone Number ID" v={n.phone_number_id || "—"} mono copyable={n.phone_number_id} onCopy={() => copy(n.phone_number_id, "Phone Number ID")} />
                    <Row k="API Key" v={n.api_key && n.api_key !== "PLACEHOLDER" ? `${n.api_key.slice(0, 6)}…${n.api_key.slice(-4)}` : "—"} mono />
                    <Row k="Webhook secret" v={n.webhook_secret ? "configurado" : "—"} />
                    <Row k="Display Name" v={n.display_name || "—"} />
                    <Row k="Quality" v={n.quality_rating ? <Badge tone={QUALITY_TONES[n.quality_rating] || "neutral"}>{n.quality_rating}</Badge> : "—"} />
                    <Row k="Última verificación" v={n.last_health_check_at ? `${new Date(n.last_health_check_at).toLocaleString()} · ${n.last_health_status || ""}` : "Nunca"} />
                    {n.notes && (
                      <div className="bg-page rounded p-2 mt-2 text-[11px] text-carbon-500 leading-relaxed">{n.notes}</div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button onClick={() => testConn(n.id)} data-testid={`test-conn-${n.id}`}
                      className="text-xs px-2.5 py-1 border border-forest text-forest rounded hover:bg-forest/5 flex items-center gap-1.5">
                      <PlugsConnected size={12} /> Probar conexión
                    </button>
                    <button onClick={() => setEdit(n)} data-testid={`edit-${n.id}`}
                      className="text-xs px-2.5 py-1 border border-carbon-200 text-carbon-700 rounded hover:bg-page flex items-center gap-1.5">
                      <PencilSimple size={12} /> Editar
                    </button>
                    <button onClick={() => remove(n.id)} className="text-xs px-2.5 py-1 text-signal hover:bg-signal/5 rounded ml-auto flex items-center gap-1.5">
                      <Trash size={12} />
                    </button>
                  </div>
                </Card>
              );
            })}
            {items.length === 0 && (
              <Card className="p-8 text-center text-carbon-500 md:col-span-2">
                No hay números configurados. Agrega uno para empezar a recibir mensajes.
              </Card>
            )}
          </div>
        )}

        {/* Routing diagnostic helper */}
        <Card className="p-5 mt-6" testid="routing-help-card">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-carbon-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="section-label mb-1">¿Cómo funciona el routing entre números?</div>
              <ol className="text-sm text-carbon-600 space-y-1.5 list-decimal list-inside leading-relaxed">
                <li>Un lead manda WhatsApp a tu número de <strong className="text-terracotta">Founders</strong> → 360Dialog envía el inbound con su <code className="font-mono text-xs bg-page px-1 py-0.5 rounded">phone_number_id</code>.</li>
                <li>El backend hace <code className="font-mono text-xs bg-page px-1 py-0.5 rounded">lookup_pipeline_by_phone_number_id()</code> y encuentra: pipeline=<strong className="text-terracotta">founder</strong>.</li>
                <li>El UnifiedUser se crea/actualiza con <code className="font-mono text-xs bg-page px-1 py-0.5 rounded">pipeline_slug=&quot;founder&quot;</code> y entra al funnel de Founders.</li>
                <li>Cuando le respondes, el provider usa la <strong>misma</strong> API key del número Founder. <strong>Nunca</strong> se contamina con el número de Ambassadors.</li>
              </ol>
            </div>
          </div>
        </Card>
      </PageBody>
      {(showCreate || edit) && (
        <WAForm initial={edit} onClose={() => { setEdit(null); setShowCreate(false); }} onSaved={() => { reload(); setEdit(null); setShowCreate(false); }} />
      )}
    </>
  );
}

const Row = ({ k, v, mono, copyable, onCopy }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-carbon-500 shrink-0">{k}</span>
    <span className={`text-carbon-700 flex items-center gap-1.5 ${mono ? "font-mono text-[11px]" : ""}`}>
      {v}
      {copyable && (
        <button onClick={onCopy} className="text-carbon-400 hover:text-terracotta" title="Copiar">
          <Copy size={11} />
        </button>
      )}
    </span>
  </div>
);

function WAForm({ initial, onClose, onSaved }) {
  const { pipelines } = usePipeline();
  const selectablePipelines = (pipelines || []).filter((p) => p.slug !== "all");
  const [form, setForm] = useState({
    label: initial?.label || "",
    phone_number: initial?.phone_number || "",
    phone_number_id: initial?.phone_number_id || "",
    pipeline_slug: initial?.pipeline_slug || "founder",
    api_key: initial?.api_key === "PLACEHOLDER" ? "" : initial?.api_key || "",
    webhook_secret: initial?.webhook_secret || "",
    display_name: initial?.display_name || "",
    provider: initial?.provider || "dialog360",
    status: initial?.status || "pending",
    default_lead_origin: initial?.default_lead_origin || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean empty optionals so backend stores null
      ["phone_number_id", "api_key", "webhook_secret", "display_name", "notes", "default_lead_origin"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      if (initial) await api.patch(`/whatsapp-numbers/${initial.id}`, payload);
      else await api.post(`/whatsapp-numbers`, payload);
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-surface border border-carbon-200 rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="wa-form">
        <div className="section-label mb-1">Multi-WhatsApp · 360Dialog</div>
        <h2 className="font-heading text-2xl mb-4">{initial ? "Editar número" : "Agregar número WhatsApp"}</h2>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Etiqueta interna</span>
            <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Founders Main" data-testid="wa-label-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Pipeline destino</span>
            <select value={form.pipeline_slug} onChange={(e) => setForm({ ...form, pipeline_slug: e.target.value })}
              data-testid="wa-pipeline-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
              {selectablePipelines.map((p) => (
                <option key={p.slug} value={p.slug}>{p.short || p.label}</option>
              ))}
            </select>
            <span className="text-[10px] text-carbon-500 mt-1 block">Todos los inbound por este número irán a este pipeline.</span>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Provider</span>
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
              data-testid="wa-provider-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
              <option value="dialog360">360Dialog (producción)</option>
              <option value="mock">Mock (desarrollo / sin envío real)</option>
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Número (E.164) *</span>
            <input required value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              placeholder="+50239927035" data-testid="wa-phone_number-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 font-mono text-xs" />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">
              Phone Number ID <span className="text-signal normal-case">(crítico para routing)</span>
            </span>
            <input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
              placeholder="123456789012345" data-testid="wa-phone_number_id-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 font-mono text-xs" />
            <span className="text-[10px] text-carbon-500 mt-1 block">El ID que envía 360Dialog/Meta en cada webhook. Sin esto el routing no funciona.</span>
          </label>

          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">API Key 360Dialog</span>
            <div className="relative mt-1">
              <input type={showKey ? "text" : "password"} value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="D360-XXXX..." data-testid="wa-api_key-input"
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 pr-10 font-mono text-xs" />
              <button type="button" onClick={() => setShowKey((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-carbon-500 hover:text-carbon-900">
                {showKey ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <span className="text-[10px] text-carbon-500 mt-1 block">Header <code className="font-mono">D360-API-KEY</code> que usaremos para enviar mensajes salientes de este pipeline.</span>
          </label>

          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Webhook secret (opcional)</span>
            <input value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
              placeholder="Cadena aleatoria para validar X-Hub-Signature-256" data-testid="wa-webhook_secret-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 font-mono text-xs" />
            <span className="text-[10px] text-carbon-500 mt-1 block">Si lo configuras en 360Dialog, también ponlo en <code className="font-mono">WHATSAPP_WEBHOOK_SECRET</code> en el backend.</span>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Display Name (Meta)</span>
            <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="Heroes Migrantes" data-testid="wa-display_name-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Estado</span>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="wa-status-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
              <option value="active">Activo</option>
              <option value="pending">Pendiente</option>
              <option value="inactive">Inactivo</option>
            </select>
          </label>

          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Origen por defecto del lead (opcional)</span>
            <input value={form.default_lead_origin || ""} onChange={(e) => setForm({ ...form, default_lead_origin: e.target.value })}
              placeholder="ej: whatsapp_founders"
              data-testid="wa-default-origin-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono" />
            <span className="text-[10px] text-carbon-500 mt-1 block">Los leads que entren por este número se etiquetan con este origen (si no traen uno por keyword).</span>
          </label>

          <label className="block col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Notas</span>
            <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-xs" rows={2} />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-carbon-500 hover:text-carbon-700">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="wa-save-btn"
            className="text-sm bg-terracotta text-white px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
